import { create } from 'zustand'
import { completedNoticeRemainingMs } from '@/lib/parkedStream'
import {
  syncPersistedStream,
  writeDismissedBannerKey,
} from '@/lib/persistedStream'
import type { FileArtifact } from '@/types/chat'

export interface PinnedReportRow {
  serviceProvider?: string
  metric?: string
  value: string
  confidence: 'high' | 'medium' | 'low'
  source: string
  note: string
}

export interface PinnedReport {
  id: string
  title: string
  type: string
  query?: string
  createdAt: string
  rows: PinnedReportRow[]
}

export interface AnalysisTable {
  title: string
  caption?: string
  columns: string[]
  rows: Array<Record<string, any>>
}

export interface AnalysisDriver {
  name: string
  value: string
  status: 'critical' | 'degrading' | 'stable' | 'good'
  detail: string
}

export interface WorkflowStep {
  id: string
  label: string
  icon?: string
  status: 'done' | 'active' | 'pending'
  duration?: number
  thoughts?: string[]
  tools?: Array<{ name: string; meta?: string; icon?: string }>
  sql_query?: string
}

export interface DynamicSection {
  title: string
  content: string
  /** Prose with no heading — not a numbered SUMMARY, table, or chart. */
  untitled?: boolean
}

export interface UserResponseOption {
  id?: string
  label: string
  description?: string
}

export interface UserResponseSection {
  header?: string
  question: string
  options: UserResponseOption[]
  allow_freetext?: boolean
  answered?: boolean
  selectedOption?: string
  tool_use_id?: string
  /** Backend: question is still open — do not pre-select an option. */
  pendingUserResponse?: boolean
}

export interface ActiveAnalysis {
  id: string
  chatId?: string
  /** From ChatRead / history; drives the evidence-panel Star / Unstar label. */
  starred?: boolean
  responseId?: string
  title: string
  subtitle?: string
  question: string
  persona?: string
  timestamp?: string
  explanation: string
  scope: string
  metric: string
  filter: string
  grain: string
  stepsCount: number
  timeSec: string
  summary: string
  /** First ## Summary / Overview label from kind=text (not a static SUMMARY). */
  summaryTitle?: string
  isLoading?: boolean
  /** Live stream: at least one block_delta kind=text has been appended. */
  hasTextDelta?: boolean
  /** API abort — a content_block had `synthetic: true`. */
  wasStreamInterrupted?: boolean
  /** Assistant `parent_response_id` — Retry sends this as `regenerate_of`. */
  parentResponseId?: string
  /** kind=text body with <understanding> stripped — shown after the trace. */
  answerText?: string
  traceSteps?: WorkflowStep[]
  /** Steps taken AFTER an answered `ask_user`, kept separate from
   * `traceSteps` so the turn reads: work → the clarification → more work. */
  continuationSteps?: WorkflowStep[]
  /** Multi-clarify turns: work streamed after the latest ask_user card. */
  postClarifyTraceSteps?: WorkflowStep[]
  table?: AnalysisTable
  chart?: any
  userResponseSection?: UserResponseSection
  /** Earlier answered ask_user in the same assistant turn (before a later pending one). */
  priorAnsweredClarification?: UserResponseSection
  analysisText?: string
  otherGapsText?: string
  recommendedPlay?: string
  drivers?: AnalysisDriver[]
  recommendations?: string[]
  suggestedQuestions?: string[]
  toolTraces?: Array<{
    meta?: string | null
    text?: string
    status?: string
    message?: string
    toolUseId?: string
    duration_ms?: number | null
  }>
  /** Deliverables the turn wrote under /mnt/outputs — evidence pane only. */
  files?: FileArtifact[]
  /** Regenerate siblings of this answer, oldest first — drives `‹ 1 2 ›`. */
  siblingIds?: string[]
  siblingIndex?: number
  queryDatasets?: Array<{
    id?: string
    path: string
    title: string
    sql_query: string
  }>
  dynamicSections?: DynamicSection[]
  rawMessages?: Array<any>
  allAnalyses?: ActiveAnalysis[]
}

export interface ChatTurnMessage {
  id: string
  sender: 'user' | 'assistant'
  text: string
  timestamp: string
  summaryMetrics?: { label: string; value: string; badge?: string }[]
  suggestedQuestions?: string[]
}

export interface ChatSession {
  id: string
  title: string
  timestamp: string
  messages: ChatTurnMessage[]
  analysis?: ActiveAnalysis
}

export interface ActiveStreamSession {
  chatId: string | null
  query: string
  messages: ChatTurnMessage[]
  activeAnalysis: ActiveAnalysis
  isStreaming: boolean
  isAborted?: boolean
  /** Retry / Restart on an existing turn — wait for new kind=text. */
  isStreamResume?: boolean
  /** Restored from stream_status after a page reload. Header keeps the banner. */
  restoredOnLoad?: boolean
  /** New question or Retry after the user had closed the banner. */
  showForUserAction?: boolean
  /** Assistant slot this session is streaming or paused on. */
  assistantMsgId?: string
  /**
   * Stream finished while this chat was not the open route.
   * Banner stays until Return, X, a new stream, or one minute.
   */
  isCompleted?: boolean
  /** When the completed-away banner started. Cleared one minute later. */
  completedAt?: number
}

export interface ClosedEvidence {
  chatId: string | null
  question: string
  title?: string
  analysis: ActiveAnalysis | null
  messages: ChatTurnMessage[]
}

interface VisionState {
  persona: string
  setPersona: (persona: string) => void

  activeAnalysis: ActiveAnalysis | null
  setActiveAnalysis: (analysis: ActiveAnalysis | null) => void
  clearAnalysis: () => void

  isStreaming: boolean
  setIsStreaming: (streaming: boolean) => void

  activeStreamSession: ActiveStreamSession | null
  setActiveStreamSession: (
    session:
      | ActiveStreamSession
      | null
      | ((prev: ActiveStreamSession | null) => ActiveStreamSession | null),
  ) => void
  isInspectingHistory: boolean
  setIsInspectingHistory: (inspecting: boolean) => void

  streamingChatIds: string[]
  activeStreams: Record<string, ActiveStreamSession>
  setStreamSession: (chatId: string, session: ActiveStreamSession | null) => void

  latestAbortedChatId: string | null
  abortedChatIds: string[]
  markChatAborted: (chatId: string) => void
  unmarkChatAborted: (chatId: string) => void

  loadingChatId: string | null
  setLoadingChatId: (loadingChatId: string | null) => void

  isEvidenceHidden: boolean
  setIsEvidenceHidden: (hidden: boolean) => void

  closedEvidence: ClosedEvidence | null
  setClosedEvidence: (closed: ClosedEvidence | null) => void

  reports: PinnedReport[]
  addReport: (report: PinnedReport) => void
  removeReport: (id: string) => void

  messages: ChatTurnMessage[]
  addChatMessage: (msg: ChatTurnMessage) => void
  resetChat: () => void

  history: ChatSession[]
  loadHistorySession: (id: string) => void
}

const loadInitialReports = (): PinnedReport[] => {
  try {
    const stored = localStorage.getItem('vision_pinned_reports')
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

const loadInitialLatestAbortedChatId = (): string | null => {
  try {
    const direct = localStorage.getItem('vision_latest_aborted_chat_id')
    if (direct) return direct
    const stored = localStorage.getItem('vision_aborted_chat_ids')
    const list = stored ? JSON.parse(stored) : []
    return Array.isArray(list) && list.length > 0 ? list[list.length - 1] : null
  } catch {
    return null
  }
}

const persistLatestAbortedChatId = (id: string | null) => {
  try {
    if (id) {
      localStorage.setItem('vision_latest_aborted_chat_id', id)
      localStorage.setItem('vision_aborted_chat_ids', JSON.stringify([id]))
    } else {
      localStorage.removeItem('vision_latest_aborted_chat_id')
      localStorage.setItem('vision_aborted_chat_ids', JSON.stringify([]))
    }
  } catch {}
}

function liveStreamIds(
  streams: Record<string, ActiveStreamSession>,
): string[] {
  return Object.keys(streams).filter((id) => streams[id]?.isStreaming)
}

function latestLiveSession(
  streams: Record<string, ActiveStreamSession>,
): ActiveStreamSession | null {
  const ids = liveStreamIds(streams)
  const id = ids[ids.length - 1]
  return id ? (streams[id] ?? null) : null
}

/** Keep a live, aborted, or completed-away notice. Drop a turn the user watched finish. */
function noticeSession(
  next: ActiveStreamSession | null,
): ActiveStreamSession | null {
  if (!next) return null
  if (next.isStreaming || next.isAborted) return next
  if (next.isCompleted) {
    return next.completedAt ? next : { ...next, completedAt: Date.now() }
  }
  return null
}

let completedNoticeTimer: ReturnType<typeof setTimeout> | null = null

function clearCompletedNoticeTimer() {
  if (!completedNoticeTimer) return
  clearTimeout(completedNoticeTimer)
  completedNoticeTimer = null
}

/** Drop a finished banner one minute after it completed, unless a newer stream replaced it. */
function scheduleCompletedNoticeExpiry(session: ActiveStreamSession | null) {
  clearCompletedNoticeTimer()
  if (!session?.isCompleted || session.isStreaming || session.isAborted) return
  const completedAt = session.completedAt
  if (!completedAt) return
  const remaining = completedNoticeRemainingMs(completedAt)
  const delay = Math.max(0, remaining ?? 0)
  const chatId = session.chatId
  completedNoticeTimer = setTimeout(() => {
    completedNoticeTimer = null
    const current = useVisionStore.getState().activeStreamSession
    if (
      current?.isCompleted &&
      !current.isStreaming &&
      current.chatId === chatId &&
      current.completedAt === completedAt
    ) {
      useVisionStore.getState().setActiveStreamSession(null)
    }
  }, delay)
}

export const useVisionStore = create<VisionState>((set, get) => ({
  persona: 'Leadership',
  setPersona: (persona) => set({ persona }),

  activeAnalysis: null,
  setActiveAnalysis: (analysis) => set({ activeAnalysis: analysis }),
  clearAnalysis: () => set({ activeAnalysis: null, isEvidenceHidden: false }),

  isEvidenceHidden: false,
  setIsEvidenceHidden: (hidden) => set({ isEvidenceHidden: hidden }),

  closedEvidence: null,
  setClosedEvidence: (closed) => set({ closedEvidence: closed }),

  isStreaming: false,
  setIsStreaming: (streaming) => set({ isStreaming: streaming }),

  activeStreamSession: null,
  setActiveStreamSession: (session) => {
    let scheduled: ActiveStreamSession | null = null
    set((s) => {
      const next =
        typeof session === 'function' ? session(s.activeStreamSession) : session
      if (!next) {
        const live = latestLiveSession(s.activeStreams)
        syncPersistedStream(live)
        scheduled = live
        return { activeStreamSession: live }
      }
      if (next.chatId) {
        const nextStreams = { ...s.activeStreams }
        if (next.isStreaming) nextStreams[next.chatId] = next
        else delete nextStreams[next.chatId]
        const streamingIds = liveStreamIds(nextStreams)
        const live = latestLiveSession(nextStreams)
        const activeStreamSession = live ?? noticeSession(next)
        syncPersistedStream(activeStreamSession)
        scheduled = activeStreamSession
        return {
          activeStreams: nextStreams,
          streamingChatIds: streamingIds,
          activeStreamSession,
        }
      }
      const activeStreamSession = noticeSession(next)
      syncPersistedStream(activeStreamSession)
      scheduled = activeStreamSession
      return { activeStreamSession }
    })
    scheduleCompletedNoticeExpiry(scheduled)
  },
  streamingChatIds: [],
  activeStreams: {},
  setStreamSession: (chatId, session) => {
    let scheduled: ActiveStreamSession | null = null
    set((s) => {
      const nextStreams = { ...s.activeStreams }
      if (!session || !session.isStreaming) {
        delete nextStreams[chatId]
      } else {
        nextStreams[chatId] = session
      }
      const streamingIds = liveStreamIds(nextStreams)
      const live = latestLiveSession(nextStreams)
      const parkedNotice =
        !live &&
        s.activeStreamSession &&
        !s.activeStreamSession.isStreaming &&
        (s.activeStreamSession.isAborted || s.activeStreamSession.isCompleted)
          ? s.activeStreamSession
          : null
      const activeStreamSession = live ?? parkedNotice
      syncPersistedStream(activeStreamSession)
      scheduled = activeStreamSession
      return {
        activeStreams: nextStreams,
        streamingChatIds: streamingIds,
        activeStreamSession,
      }
    })
    scheduleCompletedNoticeExpiry(scheduled)
  },
  latestAbortedChatId: loadInitialLatestAbortedChatId(),
  abortedChatIds: loadInitialLatestAbortedChatId()
    ? [loadInitialLatestAbortedChatId()!]
    : [],
  markChatAborted: (chatId) =>
    set(() => {
      persistLatestAbortedChatId(chatId)
      return { latestAbortedChatId: chatId, abortedChatIds: [chatId] }
    }),
  unmarkChatAborted: (chatId) =>
    set((s) => {
      if (s.latestAbortedChatId === chatId) {
        persistLatestAbortedChatId(null)
        return { latestAbortedChatId: null, abortedChatIds: [] }
      }
      return s
    }),
  isInspectingHistory: false,
  setIsInspectingHistory: (inspecting) =>
    set({ isInspectingHistory: inspecting }),

  loadingChatId: null,
  setLoadingChatId: (loadingChatId) => set({ loadingChatId }),

  reports: loadInitialReports(),
  addReport: (report) =>
    set((s) => {
      const nextReports = [report, ...s.reports]
      try {
        localStorage.setItem(
          'vision_pinned_reports',
          JSON.stringify(nextReports),
        )
      } catch {}
      return { reports: nextReports }
    }),
  removeReport: (id) =>
    set((s) => {
      const nextReports = s.reports.filter((r) => r.id !== id)
      try {
        localStorage.setItem(
          'vision_pinned_reports',
          JSON.stringify(nextReports),
        )
      } catch {}
      return { reports: nextReports }
    }),

  messages: [],
  addChatMessage: (msg) => set((s) => ({ messages: [...s.messages, msg] })),
  resetChat: () => {
    writeDismissedBannerKey(null)
    set({
      messages: [],
      activeAnalysis: null,
      isEvidenceHidden: false,
      closedEvidence: null,
      isInspectingHistory: false,
      isStreaming: false,
      loadingChatId: null,
    })
  },

  history: [],
  loadHistorySession: (id) => {
    const session = get().history.find((h) => h.id === id)
    if (session) {
      set({
        messages: session.messages,
        activeAnalysis: session.analysis || null,
        isEvidenceHidden: false,
      })
    }
  },
}))
